import { Box, Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ZONE_COLORS = {
  loading: 'rgba(74, 222, 128, 0.8)',
  dumping: 'rgba(251, 191, 36, 0.8)',
  waiting: 'rgba(255, 140, 0, 0.8)',
  fuel: 'rgba(50, 205, 50, 0.8)',
} as const;

interface MapLegendProps {
  enableDarkTheme: boolean;
  pinSize?: number;
  boxHeight?: number;
  fontSize?: number;
  showPitAreas?: boolean;
  showSiteAreas?: boolean;
  showTrafficEvents?: boolean;
  gap?: number;
  customerCode?: string;
}

const MapLegend: React.FC<MapLegendProps> = ({
  enableDarkTheme,
  pinSize = 16,
  boxHeight = 11,
  fontSize = 12,
  showPitAreas = true,
  showSiteAreas = false,
  showTrafficEvents = false,
  gap = 1,
  customerCode = '',
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullScreen = !!(
        document.fullscreenElement ??
        (document as any).webkitFullscreenElement ??
        (document as any).mozFullScreenElement ??
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isFullScreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Check initial state
    handleFullscreenChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange
      );
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexShrink: 0,
        gap,
        backgroundColor: enableDarkTheme ? palette.boxBackground : '#f3f4f2',
        padding: '8px 12px',
        borderRadius: 2,
        border: `solid 1px ${
          enableDarkTheme ? palette.borderGrey : 'rgba(0,0,0,0.12)'
        }`,
        marginLeft: isFullscreen ? 0 : 2,
        width: 'fit-content',
        marginTop: isFullscreen ? '10px' : '3px',
        height: 'fit-content',
        minHeight: `${boxHeight}px`,
        transition: 'height 0.2s ease-in-out',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 80 : 'auto',
        left: isFullscreen ? 10 : 'auto',
        zIndex: isFullscreen ? 1000 : 'auto',
        maxWidth: isFullscreen ? 'calc(100vw - 20px)' : 'calc(100% - 20px)',
        flexWrap: isFullscreen ? 'wrap' : 'nowrap',
      }}
    >
      {showPitAreas && (
        <>
          {[
            { color: ZONE_COLORS.loading, label: t('loading_area') },
            { color: ZONE_COLORS.dumping, label: t('dumping_area') },
          ].map(({ color, label }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap,
                marginTop: isFullscreen ? '-12px' : '0px',
              }}
            >
              <Box
                sx={{
                  width: pinSize / 2,
                  height: pinSize / 2,
                  backgroundColor: color,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255, 0.3)',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: `${fontSize}px`,
                  color: enableDarkTheme
                    ? ThemePalette.typography.wheat
                    : ThemePalette.typography.black,
                }}
                whiteSpace="nowrap"
              >
                {label}
              </Typography>
            </Box>
          ))}
        </>
      )}
      {showSiteAreas && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap,
            marginLeft: '10px',
          }}
        >
          {(customerCode === 'car'
            ? [
                // All areas drawn from the location-importer geohashes.
                {
                  color: 'rgba(30, 144, 255, .8)', // DodgerBlue
                  label: t('loading'),
                  shape: 'square' as const,
                },
                {
                  color: 'rgba(220, 20, 60, .8)', // Crimson
                  label: t('dumping'),
                  shape: 'square' as const,
                },
                {
                  color: 'rgba(236, 72, 153, .8)', // Pink
                  label: t('stockpile'),
                  shape: 'square' as const,
                },
                {
                  color: 'rgba(6, 182, 212, .8)', // Cyan
                  label: t('crusher'),
                  shape: 'square' as const,
                },
                {
                  color: 'rgba(245, 158, 11, .8)', // Amber
                  label: t('intersection_area'),
                  shape: 'square' as const,
                },
                {
                  color: 'rgba(128, 191, 175, .8)', // Teal
                  label: t('parking_area'),
                  shape: 'square' as const,
                },
                {
                  color: ZONE_COLORS.fuel,
                  label: t('fuel_area'),
                  shape: 'square' as const,
                },
                {
                  color: 'rgba(147, 112, 219, .8)', // MediumPurple
                  label: t('workshop_area'),
                  shape: 'square' as const,
                },
              ]
            : [
                {
                  color: ZONE_COLORS.waiting,
                  label: t('waiting_area'),
                  shape: 'circle' as const,
                },
                {
                  color: ZONE_COLORS.fuel,
                  label: t('fuel_area'),
                  shape: 'circle' as const,
                },
              ]
          ).map(({ color, label, shape }) => (
            <Box
              key={label}
              sx={{ display: 'flex', alignItems: 'center', gap }}
            >
              <Box
                sx={{
                  width: pinSize / 2,
                  height: pinSize / 2,
                  backgroundColor: color,
                  borderRadius: shape === 'square' ? '2px' : '50%',
                  border: '1px solid rgba(255,255,255, 0.3)',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: `${fontSize}px`,
                  color: enableDarkTheme
                    ? ThemePalette.typography.wheat
                    : ThemePalette.typography.black,
                }}
                whiteSpace="nowrap"
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {showTrafficEvents && (
        <>
          {[
            {
              color: 'rgba(248, 113, 113, 0.8)',
              label: t('traffic_over_capacity'),
            },
          ].map(({ color, label }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap,
                marginTop: isFullscreen ? '-12px' : '0px',
              }}
            >
              <Box
                sx={{
                  width: pinSize / 2,
                  height: pinSize / 2,
                  backgroundColor: color,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255, 0.3)',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: `${fontSize}px`,
                  color: enableDarkTheme
                    ? ThemePalette.typography.wheat
                    : ThemePalette.typography.black,
                }}
                whiteSpace="nowrap"
              >
                {label}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default MapLegend;
